import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { type FormEvent, useEffect, useRef, useState } from "react";

import { ApiError, submitOnboarding, type SpifPayoutType } from "../../api/client";
import { useCurrentUser } from "../../auth/useCurrentUser";
import { OnboardingBackButton, OnboardingShell } from "../../components/OnboardingShell";
import { Button } from "../../components/ui/button";
import { CHANNEL_PARTNER } from "../../lib/role-labels";
import { FormError } from "@/components/ui/form-error";

type OptIn = "yes" | "no" | "";
type PayoutChoice = Exclude<SpifPayoutType, "none"> | "";

/**
 * Step 2 of the post-invite onboarding wizard — SPIF preference.
 *
 * Per the spec, the distribution method is conditionally rendered only
 * when the partner opts in. Opting out persists `spif_opt_in=false` and
 * `spif_payout_type=none`.
 */
export function OnboardingSpifStep() {
  const navigate = useNavigate();
  const me = useCurrentUser();

  const [optIn, setOptIn] = useState<OptIn>("");
  const [payout, setPayout] = useState<PayoutChoice>("");
  const [topError, setTopError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const opt = optIn === "yes";
      const payout_type: SpifPayoutType = opt ? (payout as SpifPayoutType) : "none";
      return submitOnboarding(me.accessToken!, {
        spif_opt_in: opt,
        spif_payout_type: payout_type,
      });
    },
    onSuccess: () => {
      void navigate({ to: "/onboarding/terms" });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 422) {
        setTopError("That choice isn't valid. Pick one of the options above.");
        return;
      }
      setTopError("Couldn't save your choice. Try again.");
    },
  });

  // CHP-1105: resolve the guard redirect as a value + fire it from a ref-guarded
  // effect instead of returning a render-time <Navigate>, which flashes the
  // wrong frame and can re-navigate on every re-render (the CHP-1203 loop).
  // `null` means "stay on this step". Decision order preserved exactly from the
  // sequential guards below. SPIF is a partner-org preference set by the org's
  // signing PO (Flow A); the last two branches are defence-in-depth for a URL-
  // hop straight to /onboarding/spif:
  //   - Admins/managers have no rewards step but DO sign T&Cs → /terms (slice 1a).
  //   - CHP-1021: a non-signing partner (a BDM, or an additional PO of an
  //     already-registered org) has NO rewards or T&C step (the founding PO
  //     answered both) → back to their single Identity step; submitting it
  //     stamps the org-inherited acceptance and they leave the wizard.
  const redirectTarget: string | null =
    me.isAuthLoading || me.isLoading
      ? null
      : !me.isAuthenticated
        ? "/login"
        : me.data?.tc_accepted_at != null
          ? "/dashboard"
          : me.data && me.data.role !== "channel_partner"
            ? "/onboarding/terms"
            : me.data && (me.data.is_org_admin === false || me.data.org_already_registered === true)
              ? "/onboarding/identity"
              : null;

  const redirected = useRef(false);
  useEffect(() => {
    if (redirectTarget === null || redirected.current) return;
    redirected.current = true;
    void navigate({ to: redirectTarget, replace: true });
  }, [redirectTarget, navigate]);

  if (me.isAuthLoading || me.isLoading) {
    return <p role="status">Loading…</p>;
  }
  // Redirect pending → loader, never a flash of the wrong route.
  if (redirectTarget !== null) {
    return <p role="status">Loading…</p>;
  }

  // CHP-1175: the preference is stored on the Organisation, so a signing PO
  // whose account has no linked org yet (invited without a company, or
  // unlinked by an admin) has nowhere to store it — the backend 409s the
  // write. Render an explanatory step instead of a dead-end form; they can
  // set the preference later (Insights → Rewards Model) once linked.
  if (me.data?.organization == null) {
    return (
      <OnboardingShell step={2} title="Rewards Model">
        <p className="text-sm text-muted-foreground" data-testid="spif-orgless-notice">
          Rewards preferences are set for your whole organisation. Your account isn&apos;t linked to
          an organisation yet — a ResetData administrator can link it for you. You can continue
          onboarding now and choose your rewards model later from Insights once the link is in
          place.
        </p>
        <div className="flex gap-3">
          <OnboardingBackButton step={2} />
          <Button
            type="button"
            size="lg"
            className="flex-1"
            data-testid="spif-next"
            onClick={() => void navigate({ to: "/onboarding/terms" })}
          >
            Next
          </Button>
        </div>
      </OnboardingShell>
    );
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setTopError(null);
    if (optIn === "") {
      setTopError("Choose Yes or No to continue.");
      return;
    }
    if (optIn === "yes" && payout === "") {
      setTopError("Choose how your bonus rewards should be distributed.");
      return;
    }
    save.mutate();
  }

  return (
    <OnboardingShell step={2} title="Rewards Model">
      <p className="text-sm text-muted-foreground">
        Bonus rewards can be earned and paid as part of the ResetData {CHANNEL_PARTNER} Program.
      </p>
      <form onSubmit={onSubmit} aria-busy={save.isPending} noValidate className="space-y-6">
        <fieldset className="space-y-2" aria-describedby={topError ? "spif-error" : undefined}>
          <legend className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
            Would you like to participate in our rewards program?
            <span aria-hidden="true"> *</span>
            <span className="sr-only"> (required)</span>
          </legend>
          <label className="flex items-start gap-3 rounded-md border p-3">
            <input
              type="radio"
              name="opt-in"
              value="yes"
              checked={optIn === "yes"}
              onChange={() => setOptIn("yes")}
              className="mt-1"
              data-testid="spif-opt-in-yes"
            />
            <span>
              <span className="font-medium">Yes</span>
              <span className="block text-xs text-muted-foreground">
                We would like to participate.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-3 rounded-md border p-3">
            <input
              type="radio"
              name="opt-in"
              value="no"
              checked={optIn === "no"}
              onChange={() => {
                setOptIn("no");
                setPayout("");
              }}
              className="mt-1"
            />
            <span>
              <span className="font-medium">No</span>
              <span className="block text-xs text-muted-foreground">
                We would not like to participate.
              </span>
            </span>
          </label>
        </fieldset>

        {optIn === "yes" && (
          <fieldset className="space-y-2" aria-describedby={topError ? "spif-error" : undefined}>
            <legend className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
              How would you like your bonus rewards distributed?
              <span aria-hidden="true"> *</span>
              <span className="sr-only"> (required)</span>
            </legend>
            <label className="flex items-start gap-3 rounded-md border p-3">
              <input
                type="radio"
                name="payout"
                value="pooled"
                checked={payout === "pooled"}
                onChange={() => setPayout("pooled")}
                className="mt-1"
                data-testid="spif-payout-pooled"
              />
              <span className="font-medium">
                Pooled as an organisation for management to distribute
              </span>
            </label>
            <label className="flex items-start gap-3 rounded-md border p-3">
              <input
                type="radio"
                name="payout"
                value="direct"
                checked={payout === "direct"}
                onChange={() => setPayout("direct")}
                className="mt-1"
              />
              <span className="font-medium">Paid directly to the individual</span>
            </label>
          </fieldset>
        )}

        {topError && (
          <FormError id="spif-error" data-testid="spif-error">
            {topError}
          </FormError>
        )}
        {/* CHP-313: Back + primary share a footer action row (idiomatic wizard
            pattern) rather than a standalone Back link in the header. */}
        <div className="flex gap-3">
          <OnboardingBackButton step={2} />
          <Button
            type="submit"
            loading={save.isPending}
            loadingLabel="Saving…"
            size="lg"
            className="flex-1"
            data-testid="spif-next"
          >
            Next
          </Button>
        </div>
      </form>
    </OnboardingShell>
  );
}
